import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Layers,
  Plus,
  Save,
  X,
} from "lucide-react";

type Member = {
  name: string;
  email: string;
  avatar: string | null;
  initials: string;
};
type Department = { id: string; name: string; members: Member[] };
type Card = {
  id: string;
  title: string;
  context: string;
  questions: string;
  decision: string;
  group: string;
  owner: string;
  readiness: string;
  discussion: string;
  pillar: string;
  objective: string;
  kpi: string;
  target: string;
  deadline: string;
  followUp: string;
};
type Preparation = {
  pillars: string[];
  frame: string;
  outcomes: string;
  cards: Card[];
};
type Snapshot = {
  department: Department;
  preparation: Preparation;
  version: number;
};
const api = "/api/prisme/department-retreats";
const readiness: Record<string, string> = {
  brouillon: "Brouillon",
  "a-completer": "À compléter",
  pret: "Prêt",
};
const discussion: Record<string, string> = {
  "a-discuter": "À discuter",
  "en-discussion": "En discussion",
  complete: "Complété",
};
const dany: Member = {
  name: "Dany Bourassa",
  email: "dany.bourassa@bzinc.ca",
  avatar: "/DB.png",
  initials: "DB",
};
const blankCard = (): Card => ({
  id: crypto.randomUUID(),
  title: "",
  context: "",
  questions: "",
  decision: "",
  group: "",
  owner: "",
  readiness: "brouillon",
  discussion: "a-discuter",
  pillar: "",
  objective: "",
  kpi: "",
  target: "",
  deadline: "",
  followUp: "",
});
async function request(path: string, init?: RequestInit) {
  const response = await fetch(path, {
    credentials: "include",
    cache: "no-store",
    ...init,
  });
  const result = await response.json();
  if (!response.ok)
    throw Object.assign(new Error(result.error || "Service indisponible"), {
      status: response.status,
    });
  return result;
}
function Modal({
  children,
  label,
  onClose,
  className,
}: {
  children: ReactNode;
  label: string;
  onClose: () => void;
  className: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const dialog = ref.current!;
    const overflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      before?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={label}
      className={className}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Tab") return;
        const nodes = Array.from(
          ref.current!.querySelectorAll<HTMLElement>(
            "button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),a[href]",
          ),
        ).filter((n) => n.getClientRects().length);
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }}
    >
      {children}
    </dialog>
  );
}
function Avatar({ member }: { member: Member }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      {member.avatar && !failed ? (
        <img
          className="h-9 w-9 rounded-full object-cover ring-2 ring-white"
          src={member.avatar}
          alt=""
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-100 text-xs font-bold text-cyan-900">
          {member.initials}
        </span>
      )}
      {member.name}
    </span>
  );
}
export function DepartmentRetreatEntry({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let active = true;
    setVisible(false);
    request(api)
      .then((r) => {
        if (active) setVisible(r.departments.length > 0);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user?.email]);
  return visible ? <>{children}</> : null;
}
export default function DepartmentRetreats() {
  const { user } = useAuth();
  const { department } = useParams();
  // Remount editors and invalidate every in-flight response on route/account change.
  return (
    <Workspace
      key={`${user?.email}:${department || "hub"}`}
      departmentId={department}
      actorEmail={user?.email || ""}
    />
  );
}
// In-memory navigation recovery only: never localStorage, never public assets.
// Rehydrate only after a fresh successful server authorization; keep the original
// version so recovered stale drafts cannot overwrite a colleague's preparation.
const navigationDrafts = new Map<
  string,
  { version: number; preparation: Preparation }
>();
function Workspace({
  departmentId,
  actorEmail,
}: {
  departmentId?: string;
  actorEmail: string;
}) {
  const recoveryKey = `${actorEmail}:${departmentId || "hub"}`;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [draft, setDraft] = useState<Preparation | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const alive = useRef(true);
  const [view, setView] = useState<"liste" | "mur">("liste");
  const [filter, setFilter] = useState("tous");
  const [editor, setEditor] = useState<Card | null>(null);
  const [tab, setTab] = useState("Sujet");
  const [present, setPresent] = useState<string | null>(null);
  const [pillar, setPillar] = useState("");
  useEffect(() => {
    alive.current = true;
    request(departmentId ? `${api}/${encodeURIComponent(departmentId)}` : api)
      .then((r) => {
        if (!alive.current) return;
        if (departmentId) {
          const recovered = navigationDrafts.get(recoveryKey);
          setSnapshot(recovered ? { ...r, version: recovered.version } : r);
          setDraft(recovered?.preparation || r.preparation);
          if (recovered) {
            setDirty(true);
            setError(
              recovered.version === r.version
                ? "Brouillon non enregistré restauré après navigation. Enregistrez-le sur le serveur."
                : "Brouillon restauré, mais la version serveur a changé. Copiez vos changements puis rechargez avant de réessayer.",
            );
          }
        } else setDepartments(r.departments);
        setLoaded(true);
      })
      .catch((e) => {
        if (alive.current) {
          setError(e.message);
          setDenied(e.status === 401 || e.status === 403);
          if (e.status === 401 || e.status === 403)
            navigationDrafts.delete(recoveryKey);
          setLoaded(true);
        }
      });
    return () => {
      alive.current = false;
    };
  }, [departmentId, recoveryKey]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  function change(value: Preparation) {
    if (snapshot)
      navigationDrafts.set(recoveryKey, {
        version: snapshot.version,
        preparation: value,
      });
    setDraft(value);
    setDirty(true);
  }
  async function save() {
    if (!draft || !snapshot || busy || denied) return;
    setBusy(true);
    setError("");
    try {
      const next = await request(`${api}/${departmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ version: snapshot.version, preparation: draft }),
      });
      if (alive.current) {
        navigationDrafts.delete(recoveryKey);
        setSnapshot(next);
        setDraft(next.preparation);
        setDirty(false);
      }
    } catch (e) {
      if (alive.current) {
        const failure = e as Error & { status?: number };
        setError(failure.message);
        if (failure.status === 401 || failure.status === 403) {
          navigationDrafts.delete(recoveryKey);
          setDenied(true);
          setSnapshot(null);
          setDraft(null);
          setEditor(null);
          setPresent(null);
        }
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  const members = snapshot
    ? [
        ...snapshot.department.members,
        ...(snapshot.department.members.some((m) => m.email === dany.email)
          ? []
          : [dany]),
      ]
    : [];
  const groups = draft
    ? [...new Set(draft.cards.map((c) => c.group.trim() || "Sans groupe"))]
    : [];
  const cards =
    draft?.cards.filter(
      (c) =>
        filter === "tous" ||
        (filter === "incomplets" && c.readiness !== "pret") ||
        (filter === "sans-responsable" && !c.owner) ||
        (filter === "prets" && c.readiness === "pret"),
    ) || [];
  const presented = draft?.cards.find((c) => c.id === present);
  function transition(id: string, status: string) {
    if (draft)
      change({
        ...draft,
        cards: draft.cards.map((c) =>
          c.id === id ? { ...c, discussion: status } : c,
        ),
      });
  }
  const input =
    "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900";
  const button =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:border-cyan-500 disabled:opacity-50";
  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 md:p-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <Link
          className="inline-flex items-center gap-2 text-sm text-slate-600"
          to={departmentId ? "/comites/lac-a-epaule" : "/comites"}
          onClick={(e) => {
            if (
              dirty &&
              !confirm("Des changements ne sont pas enregistrés. Quitter ?")
            )
              e.preventDefault();
          }}
        >
          <ArrowLeft size={16} /> {departmentId ? "Lac à l’épaule" : "Comités"}
        </Link>
        <header className="rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 p-6 text-white md:p-8">
          <p className="text-xs uppercase tracking-[.2em] text-cyan-200">
            Décembre 2026 · dates à planifier
          </p>
          <h1 className="my-3 text-3xl font-bold">
            Lac à l’épaule{snapshot ? ` · ${snapshot.department.name}` : ""}
          </h1>
          <p className="max-w-3xl text-slate-200">
            Deux jours par département. Préparation avant le 10 décembre 2026 :
            10–20 cartes utiles, pas un produit parfait. Un cadre vivant, des
            décisions claires et des engagements pour 2027.
          </p>
        </header>
        {error && (
          <div
            role="alert"
            className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900"
          >
            {error}
            {!denied && (
              <button
                className="ml-3 underline"
                onClick={() => {
                  if (
                    !dirty ||
                    confirm("Recharger et abandonner les changements locaux ?")
                  )
                    location.reload();
                }}
              >
                Recharger
              </button>
            )}
          </div>
        )}
        {!loaded && <p role="status">Chargement de l’espace autorisé…</p>}
        {loaded && !departmentId && !denied && (
          <div className="grid gap-5 md:grid-cols-2">
            {departments.map((d) => (
              <Link
                key={d.id}
                to={`/comites/lac-a-epaule/${d.id}`}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-cyan-500 hover:shadow-md"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold">{d.name}</h2>
                  <ArrowRight size={20} />
                </div>
                <div className="flex flex-wrap gap-3">
                  {d.members.map((m) => (
                    <Avatar key={m.email} member={m} />
                  ))}
                </div>
                <p className="mt-5 text-sm text-slate-500">
                  2 jours · Décembre 2026, à planifier · Préparation partagée et
                  restreinte
                </p>
              </Link>
            ))}
          </div>
        )}
        {draft && snapshot && !denied && (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-4">
              {members.map((m) => (
                <Avatar key={m.email} member={m} />
              ))}
              <span className="ml-auto text-xs text-slate-500">
                Membres du département + Dany · stockage serveur
              </span>
            </div>
            <details className="rounded-2xl border bg-white p-5">
              <summary className="cursor-pointer font-semibold">
                Repères de préparation et résultats attendus
              </summary>
              <div className="mt-4 grid gap-5 text-sm leading-relaxed md:grid-cols-3">
                <div>
                  <h2 className="font-bold">01 · Comprendre</h2>
                  <p>
                    Maximum 30 % de passé : apprentissages, objectifs et bonis
                    TactiCE à revoir dans les outils autorisés, sans recopier de
                    données confidentielles ici. Clore les plans 2026 des
                    directeurs; les directeurs clôtureront ceux des employés
                    après le lac.
                  </p>
                </div>
                <div>
                  <h2 className="font-bold">02 · Construire</h2>
                  <p>
                    Le reste : présent et avenir. Partir du cadre puis des
                    cartes, ou regrouper les cartes pour faire émerger le cadre.
                    Jusqu’à 10 piliers, dont Gouvernance (rôles et
                    responsabilités). Finances, opérations, RH et outils sont
                    des angles possibles, pas un modèle imposé.
                  </p>
                </div>
                <div>
                  <h2 className="font-bold">03 · S’engager</h2>
                  <p>
                    Valider les priorités et grands objectifs, un premier cadre,
                    puis les objectifs SMART et KPI 2027 avec responsables,
                    cibles et échéances. Les points non résolus reçoivent un
                    prochain pas. Transfert manuel vers TactiCE après
                    validation; aucune synchronisation automatique.
                  </p>
                  <Link to="/admin-rh" className="text-blue-700 underline">
                    Outils Administration et RH
                  </Link>
                </div>
              </div>
            </details>
            <section className="rounded-2xl border bg-white p-5">
              <h2 className="mb-3 flex items-center gap-2 font-bold">
                <Layers size={18} /> Cadre départemental ·{" "}
                {draft.pillars.length}/10 piliers
              </h2>
              <div className="mb-4 flex flex-wrap gap-2">
                {draft.pillars.map((p) => (
                  <span
                    key={p}
                    className="rounded-full bg-cyan-50 px-3 py-1 text-sm text-cyan-900"
                  >
                    {p}
                    {p !== "Gouvernance" && (
                      <button
                        aria-label={`Retirer ${p}`}
                        className="ml-2"
                        disabled={busy}
                        onClick={() =>
                          change({
                            ...draft,
                            pillars: draft.pillars.filter((x) => x !== p),
                            cards: draft.cards.map((c) =>
                              c.pillar === p ? { ...c, pillar: "" } : c,
                            ),
                          })
                        }
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  aria-label="Nouveau pilier"
                  className={input}
                  maxLength={100}
                  value={pillar}
                  onChange={(e) => setPillar(e.target.value)}
                  placeholder="Ajouter un pilier libre"
                />
                <button
                  className={button}
                  disabled={
                    busy ||
                    draft.pillars.length >= 10 ||
                    !pillar.trim() ||
                    draft.pillars.includes(pillar.trim())
                  }
                  onClick={() => {
                    change({
                      ...draft,
                      pillars: [...draft.pillars, pillar.trim()],
                    });
                    setPillar("");
                  }}
                >
                  Ajouter
                </button>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold">
                  Cadre et rôles — brouillon évolutif
                  <textarea
                    disabled={busy}
                    className={`${input} mt-2`}
                    rows={3}
                    maxLength={10000}
                    value={draft.frame}
                    onChange={(e) =>
                      change({ ...draft, frame: e.target.value })
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  Priorités validées et grands objectifs 2027
                  <textarea
                    disabled={busy}
                    className={`${input} mt-2`}
                    rows={3}
                    maxLength={10000}
                    value={draft.outcomes}
                    onChange={(e) =>
                      change({ ...draft, outcomes: e.target.value })
                    }
                  />
                </label>
              </div>
            </section>
            <section className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="mr-auto text-xl font-bold">
                  Sujets · {draft.cards.length} / 10–20 visés
                </h2>
                <span className="text-sm text-slate-500">
                  {draft.cards.filter((c) => c.readiness === "pret").length}{" "}
                  prêts
                </span>
                <button
                  className={button}
                  disabled={busy}
                  onClick={() => {
                    setEditor(blankCard());
                    setTab("Sujet");
                  }}
                >
                  <Plus size={16} /> Sujet
                </button>
                <button
                  className={button}
                  aria-pressed={view === "liste"}
                  onClick={() => setView("liste")}
                >
                  Liste
                </button>
                <button
                  className={button}
                  aria-pressed={view === "mur"}
                  onClick={() => setView("mur")}
                >
                  Mur
                </button>
                <select
                  aria-label="Filtrer les sujets"
                  className="rounded-xl border p-2 text-sm"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="tous">Tous</option>
                  <option value="incomplets">À préparer</option>
                  <option value="sans-responsable">Sans responsable</option>
                  <option value="prets">Prêts</option>
                </select>
              </div>
              {!draft.cards.length && (
                <div className="rounded-2xl border-2 border-dashed bg-white p-8 text-center">
                  <p className="font-semibold">Vos sujets, votre réflexion.</p>
                  <p className="mt-2 text-sm text-slate-500">
                    Ajoutez votre première carte. Aucun sujet ou résultat fictif
                    n’est prérempli.
                  </p>
                </div>
              )}
              {groups.map((group) => {
                const items = cards.filter(
                  (c) => (c.group.trim() || "Sans groupe") === group,
                );
                return (
                  items.length > 0 && (
                    <details
                      key={group}
                      open
                      className="rounded-2xl border bg-white p-4"
                    >
                      <summary className="cursor-pointer font-semibold">
                        {group} ·{" "}
                        {items.filter((c) => c.readiness === "pret").length}/
                        {items.length} prêts
                      </summary>
                      <div
                        className={
                          view === "mur"
                            ? "mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3"
                            : "mt-3 space-y-2"
                        }
                      >
                        {items.map((c) => (
                          <article
                            key={c.id}
                            className={`rounded-xl border border-slate-200 p-4 ${view === "liste" ? "flex flex-wrap items-center gap-3" : ""}`}
                          >
                            <button
                              className="min-w-0 flex-1 text-left font-semibold text-blue-950"
                              onClick={() => {
                                setEditor({ ...c });
                                setTab("Sujet");
                              }}
                            >
                              {c.title}
                            </button>
                            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                              <span>
                                {members.find((m) => m.email === c.owner)
                                  ?.name || "Sans responsable"}
                              </span>
                              <span className="rounded-full bg-cyan-50 px-2 py-1">
                                {readiness[c.readiness]}
                              </span>
                              <span className="rounded-full bg-slate-100 px-2 py-1">
                                {discussion[c.discussion]}
                              </span>
                              {c.pillar && <span>{c.pillar}</span>}
                            </div>
                            <button
                              className={`${button} ${view === "mur" ? "mt-3" : ""}`}
                              onClick={() => setPresent(c.id)}
                            >
                              Présenter
                            </button>
                          </article>
                        ))}
                      </div>
                    </details>
                  )
                );
              })}
            </section>
            <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white/95 p-4 shadow-lg backdrop-blur">
              <p role="status" className="text-sm text-slate-600">
                {busy
                  ? "Enregistrement…"
                  : dirty
                    ? "Changements non enregistrés"
                    : "Version serveur chargée · " + snapshot.version}
              </p>
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-800 px-5 py-3 font-semibold text-white disabled:opacity-50"
                disabled={!dirty || busy}
                onClick={save}
              >
                <Save size={17} /> Enregistrer la préparation
              </button>
            </div>
          </>
        )}
      </div>
      {editor && draft && !denied && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3"
          role="presentation"
        >
          <Modal
            label="subject-title"
            onClose={() => setEditor(null)}
            className="relative m-auto flex max-h-[92vh] w-full max-w-3xl flex-col rounded-3xl bg-white p-5 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h2 id="subject-title" className="text-xl font-bold">
                Préparer un sujet
              </h2>
              <button
                className={button}
                aria-label="Fermer le sujet"
                onClick={() => setEditor(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="my-4 flex gap-2 overflow-x-auto" role="tablist">
              {["Sujet", "Pilotage", "Objectifs 2027"].map((t) => (
                <button
                  key={t}
                  className={`${button} shrink-0 ${tab === t ? "!bg-cyan-50 !border-cyan-600" : ""}`}
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="space-y-4 overflow-y-auto px-1 pb-3">
              {tab === "Sujet" &&
                (["title", "context", "questions", "decision"] as const).map(
                  (f) => (
                    <label key={f} className="block text-sm font-semibold">
                      {
                        {
                          title: "Titre",
                          context: "Contexte et constats",
                          questions: "Questions à discuter",
                          decision: "Décision recherchée",
                        }[f]
                      }
                      {f === "title" ? (
                        <input
                          autoFocus
                          className={`${input} mt-1`}
                          maxLength={500}
                          value={editor[f]}
                          onChange={(e) =>
                            setEditor({ ...editor, [f]: e.target.value })
                          }
                        />
                      ) : (
                        <textarea
                          className={`${input} mt-1`}
                          rows={3}
                          maxLength={4000}
                          value={editor[f]}
                          onChange={(e) =>
                            setEditor({ ...editor, [f]: e.target.value })
                          }
                        />
                      )}
                    </label>
                  ),
                )}
              {tab === "Pilotage" && (
                <>
                  <label className="block text-sm font-semibold">
                    Groupe libre
                    <input
                      className={`${input} mt-1`}
                      list="retreat-groups"
                      maxLength={500}
                      value={editor.group}
                      onChange={(e) =>
                        setEditor({ ...editor, group: e.target.value })
                      }
                    />
                    <datalist id="retreat-groups">
                      {groups
                        .filter((g) => g !== "Sans groupe")
                        .map((g) => (
                          <option key={g}>{g}</option>
                        ))}
                    </datalist>
                  </label>
                  {(
                    ["owner", "pillar", "readiness", "discussion"] as const
                  ).map((f) => (
                    <label key={f} className="block text-sm font-semibold">
                      {
                        {
                          owner: "Responsable",
                          pillar: "Pilier (facultatif)",
                          readiness: "Préparation",
                          discussion:
                            "Discussion — changement explicite uniquement",
                        }[f]
                      }
                      <select
                        className={`${input} mt-1`}
                        value={editor[f]}
                        onChange={(e) =>
                          setEditor({ ...editor, [f]: e.target.value })
                        }
                      >
                        {f === "owner" ? (
                          <>
                            <option value="">Sans responsable</option>
                            {members.map((m) => (
                              <option key={m.email} value={m.email}>
                                {m.name}
                              </option>
                            ))}
                          </>
                        ) : f === "pillar" ? (
                          <>
                            <option value="">Non lié</option>
                            {draft.pillars.map((p) => (
                              <option key={p}>{p}</option>
                            ))}
                          </>
                        ) : (
                          Object.entries(
                            f === "readiness" ? readiness : discussion,
                          ).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))
                        )}
                      </select>
                    </label>
                  ))}
                </>
              )}
              {tab === "Objectifs 2027" && (
                <>
                  <p className="text-sm text-slate-500">
                    Définir après réflexion; aucune cible n’est calculée ou
                    transférée automatiquement.
                  </p>
                  {(
                    [
                      "objective",
                      "kpi",
                      "target",
                      "deadline",
                      "followUp",
                    ] as const
                  ).map((f) => (
                    <label key={f} className="block text-sm font-semibold">
                      {
                        {
                          objective: "Objectif SMART",
                          kpi: "KPI / mesure",
                          target: "Cible à valider",
                          deadline: "Échéance",
                          followUp:
                            "Prochain pas / suivi (responsable dans Pilotage)",
                        }[f]
                      }
                      <input
                        type={f === "deadline" ? "date" : "text"}
                        className={`${input} mt-1`}
                        maxLength={f === "followUp" ? 4000 : 500}
                        value={editor[f]}
                        onChange={(e) =>
                          setEditor({ ...editor, [f]: e.target.value })
                        }
                      />
                    </label>
                  ))}
                </>
              )}
            </div>
            <div className="mt-3 flex justify-end gap-2 border-t pt-3">
              <button className={button} onClick={() => setEditor(null)}>
                Annuler
              </button>
              <button
                disabled={!editor.title.trim() || busy}
                className={button}
                onClick={() => {
                  change({
                    ...draft,
                    cards: draft.cards.some((c) => c.id === editor.id)
                      ? draft.cards.map((c) =>
                          c.id === editor.id ? editor : c,
                        )
                      : [...draft.cards, editor],
                  });
                  setEditor(null);
                }}
              >
                <Check size={16} /> Appliquer au brouillon
              </button>
            </div>
          </Modal>
        </div>
      )}
      {presented && !denied && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 p-3">
          <Modal
            label="presentation-title"
            onClose={() => setPresent(null)}
            className="relative m-auto flex h-[90vh] w-[94vw] max-w-7xl flex-col rounded-3xl bg-slate-950 p-5 text-white md:p-10"
          >
            <div className="flex justify-between gap-4">
              <span className="text-cyan-300">
                Atelier · {discussion[presented.discussion]}
              </span>
              <button
                aria-label="Fermer la présentation"
                onClick={() => setPresent(null)}
              >
                <X />
              </button>
            </div>
            <div className="my-auto overflow-y-auto py-8">
              <h2
                id="presentation-title"
                className="mb-8 text-3xl font-bold md:text-5xl"
              >
                {presented.title}
              </h2>
              <div className="grid gap-8 md:grid-cols-2">
                <section>
                  <h3 className="mb-3 text-sm uppercase tracking-widest text-cyan-300">
                    Questions à discuter
                  </h3>
                  <p className="whitespace-pre-wrap text-xl md:text-2xl">
                    {presented.questions || "À préparer ensemble"}
                  </p>
                </section>
                <section>
                  <h3 className="mb-3 text-sm uppercase tracking-widest text-cyan-300">
                    Décision recherchée
                  </h3>
                  <p className="whitespace-pre-wrap text-xl md:text-2xl">
                    {presented.decision || "À préciser"}
                  </p>
                </section>
              </div>
              <details className="mt-8 text-slate-300">
                <summary>Contexte et suivi</summary>
                <p className="mt-3 whitespace-pre-wrap">{presented.context}</p>
                <p>
                  {presented.followUp} {presented.deadline}
                </p>
              </details>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(discussion).map(([status, label]) => (
                <button
                  key={status}
                  disabled={busy}
                  className="rounded-xl border border-slate-500 px-3 py-2 text-sm"
                  onClick={() => transition(presented.id, status)}
                >
                  {label}
                </button>
              ))}
              <button
                className="ml-auto rounded-xl bg-cyan-700 px-4 py-2"
                onClick={() => {
                  const index = draft!.cards.findIndex((c) => c.id === present);
                  setPresent(
                    draft!.cards[(index + 1) % draft!.cards.length].id,
                  );
                }}
              >
                Sujet suivant →
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-400">
              Ouvrir ne change aucun statut. Enregistrez la préparation après
              l’atelier.
            </p>
          </Modal>
        </div>
      )}
    </div>
  );
}
